/* Vault — collection & search listing: live filtering, sorting, load more */
(() => {
  'use strict';
  if (customElements.get('tc-listing')) return;
  const { qs, qsa, debounce, parseHTML } = window.TC;

  class TcListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.bind();
      this.setupSidebarA11y();
      window.addEventListener('popstate', this.onPop = () => this.fetchAndRender(window.location.search.slice(1), false));
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      if (this.io) this.io.disconnect();
    }

    get form() {
      return qs('[data-tc-facets-form]', this);
    }

    bind() {
      const debounced = debounce(() => this.onFormChange(), 450);
      this.addEventListener('change', (e) => {
        if (!e.target.closest('[data-tc-facets-form]') && !e.target.matches('[data-tc-sort]')) return;
        if (e.target.type === 'number') debounced();
        else this.onFormChange();
      });
      this.addEventListener('input', (e) => {
        if (e.target.type === 'number' && e.target.closest('[data-tc-price-range]')) debounced();
      });
      this.addEventListener('submit', (e) => {
        if (!e.target.matches('[data-tc-facets-form]')) return;
        e.preventDefault();
        this.onFormChange();
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-tc-facet-link]');
        if (link) {
          e.preventDefault();
          this.fetchAndRender(new URL(link.href, window.location.origin).search.slice(1));
          return;
        }
        const more = e.target.closest('[data-tc-more]');
        if (more) {
          const list = more.previousElementSibling;
          const expanded = list.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', expanded);
          qs('[data-more]', more).hidden = expanded;
          qs('[data-less]', more).hidden = !expanded;
          return;
        }
        const quick = e.target.closest('[data-tc-price-max]');
        if (quick) {
          const range = quick.closest('.tc-facet__body');
          const inputs = qsa('input[type="number"]', range);
          if (inputs[0]) inputs[0].value = '';
          if (inputs[1]) inputs[1].value = quick.dataset.tcPriceMax;
          this.onFormChange();
          return;
        }
        const loadMore = e.target.closest('[data-tc-load-more]');
        if (loadMore) {
          e.preventDefault();
          this.loadMore(loadMore);
        }
      });
      this.setupInfinite();
    }

    setupSidebarA11y() {
      const drawer = qs('[data-tc-filters]', this);
      if (!drawer || !this.classList.contains('tc-listing--sidebar')) return;
      const mq = window.matchMedia('(min-width: 1100px)');
      const apply = () => {
        if (mq.matches) {
          drawer.removeAttribute('aria-hidden');
          qs('.tc-drawer__panel', drawer).removeAttribute('aria-modal');
          qs('.tc-drawer__panel', drawer).setAttribute('role', 'region');
        } else {
          if (!drawer.classList.contains('is-open')) drawer.setAttribute('aria-hidden', 'true');
          qs('.tc-drawer__panel', drawer).setAttribute('aria-modal', 'true');
          qs('.tc-drawer__panel', drawer).setAttribute('role', 'dialog');
        }
      };
      apply();
      mq.addEventListener?.('change', apply);
    }

    buildQuery() {
      const data = new FormData(this.form || undefined);
      const sort = qs('[data-tc-sort]', this);
      if (sort && !this.form) data.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value === '' || value === null) continue;
        params.append(key, value);
      }
      return params.toString();
    }

    onFormChange() {
      this.fetchAndRender(this.buildQuery());
    }

    async fetchAndRender(query, push = true) {
      const results = qs('[data-tc-results]', this);
      results.classList.add('is-loading');
      results.setAttribute('aria-busy', 'true');
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        const doc = parseHTML(await res.text());
        const fresh = qs('tc-listing', doc);
        if (!fresh) throw new Error('No listing');
        this.swap(fresh);
        if (push) history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = `${window.location.pathname}?${query}`;
      } finally {
        results.classList.remove('is-loading');
        results.removeAttribute('aria-busy');
      }
    }

    swap(fresh) {
      // Preserve open/closed facet groups and scroll position in the filter panel
      const openState = {};
      qsa('details.tc-facet', this).forEach((d) => (openState[d.id] = d.open));
      const wrap = qs('[data-tc-facets-wrap]', this);
      const scrollTop = wrap ? wrap.scrollTop : 0;
      const focusedId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

      ['[data-tc-results]', '[data-tc-active]', '[data-tc-count]', '[data-tc-apply-count]'].forEach((sel) => {
        const current = qs(sel, this);
        const next = qs(sel, fresh);
        if (current && next) current.innerHTML = next.innerHTML;
      });

      const freshWrap = qs('[data-tc-facets-wrap]', fresh);
      if (wrap && freshWrap) {
        // Keep the price inputs the user is typing in
        const typing = document.activeElement && document.activeElement.type === 'number' && wrap.contains(document.activeElement);
        if (!typing) wrap.innerHTML = freshWrap.innerHTML;
        else {
          qsa('.tc-facet__values', wrap).forEach((list, i) => {
            const n = qsa('.tc-facet__values', freshWrap)[i];
            if (n) list.innerHTML = n.innerHTML;
          });
        }
        qsa('details.tc-facet', wrap).forEach((d) => {
          if (d.id in openState) d.open = openState[d.id];
        });
        wrap.scrollTop = scrollTop;
      }

      const btn = qs('.tc-listing__filter-btn', this);
      const freshBtn = qs('.tc-listing__filter-btn', fresh);
      if (btn && freshBtn) btn.innerHTML = freshBtn.innerHTML;

      if (focusedId) {
        const el = document.getElementById(focusedId);
        if (el) el.focus({ preventScroll: true });
      }
      window.TC.init(this);
      this.setupInfinite();
    }

    async loadMore(link) {
      if (link.classList.contains('is-loading')) return;
      link.classList.add('is-loading');
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(url);
        const doc = parseHTML(await res.text());
        const grid = qs('[data-tc-grid]', this);
        const items = qsa('[data-tc-grid] > li', doc);
        const start = grid.children.length;
        items.forEach((li, i) => {
          const card = qs('[data-tc-reveal]', li);
          if (card) card.style.setProperty('--tc-i', i % 8);
          grid.appendChild(li);
        });
        const pager = qs('[data-tc-pager]', this);
        const nextPager = qs('[data-tc-pager]', doc);
        if (pager && nextPager) pager.innerHTML = nextPager.innerHTML;
        window.TC.init(grid);
        const firstNew = grid.children[start];
        const firstLink = firstNew && qs('a[href]', firstNew);
        if (firstLink && !this.infinite) firstLink.focus({ preventScroll: true });
        this.setupInfinite();
      } catch (err) {
        window.location.href = link.href;
      }
    }

    setupInfinite() {
      if (this.dataset.pagination !== 'infinite' || !('IntersectionObserver' in window)) return;
      this.infinite = true;
      if (this.io) this.io.disconnect();
      const more = qs('[data-tc-load-more]', this);
      if (!more) return;
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this.loadMore(more);
        },
        { rootMargin: '600px 0px' }
      );
      this.io.observe(more);
    }
  }
  customElements.define('tc-listing', TcListing);

  // Clamp long collection descriptions with a "read more" toggle
  qsa('[data-tc-clamp]').forEach((el) => {
    el.classList.add('is-clamped');
    if (el.scrollHeight <= el.clientHeight + 4) {
      el.classList.remove('is-clamped');
      return;
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tc-chero__more';
    btn.textContent = el.dataset.more || '+';
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', () => {
      const open = el.classList.toggle('is-clamped') === false;
      btn.setAttribute('aria-expanded', open);
      btn.textContent = open ? el.dataset.less || '−' : el.dataset.more || '+';
    });
    el.after(btn);
  });
})();
